// * Third Party
import {
  doc,
  getFirestore,
  collection,
  getDocs,
  query,
  where,
  limit,
  deleteDoc,
  writeBatch,
  updateDoc,
  deleteField,
} from "firebase/firestore/lite";

// * Firebase Config
import { app } from "./firebase";
import {
  signInWithEmailAndPassword,
  getAuth,
  sendPasswordResetEmail,
} from "firebase/auth";

// * Db
const db = getFirestore(app);

// * auth
export const auth = getAuth(app);

// All the collection names with their respective documents
const collectionNames = {
  ROLES: "roles",
  USERS: "users",
  AUTH_USERS: "authUsers",
  SCOOTERS: "scooters",
};

export const loginUser = async (email: string, password: string) => {
  const user = await signInWithEmailAndPassword(auth, email, password);
  return user;
};

export const logoutUser = async () => {
  await auth.signOut();
};

export const deleteDocument = async (
  collectionName: keyof typeof collectionNames,
  documentIds: string[]
) => {
  const promises = documentIds.map(async (documentId) => {
    const userDocRef = doc(db, collectionNames[collectionName], documentId);

    try {
      await deleteDoc(userDocRef);
    } catch (error) {
      console.error(`Error deleting document with ID ${documentId}:`, error);
    }
  });

  await Promise.all(promises);
};

// Example of a batch delete operation
async function deleteMultipleDocuments(collectionPath: string, documentIds: string[]): Promise<void> {
  const batch = writeBatch(db);
  documentIds.forEach((documentId) => {
    const docRef = doc(db, collectionPath, documentId);
    batch.delete(docRef);
  });

  await batch.commit();
}

export const getUserByEmailFromAuthentication: (
  email: string
) => Promise<{ id: string, email: string, name: string, correct: boolean } | undefined> = async (email: string) => {
  const users = await getDocs(
    query(
      collection(db, collectionNames.USERS),
      where("email", "==", email),
    )
  );
  if (!users.empty) {

    if (users.docs.length > 1) {
      return {
        correct: false,
        email: '',
        name: '',
        id: ''
      };
    } else {
      return {
        correct: true,
        name: users.docs[0].data().name,
        email: users.docs[0].data().email,
        id: users.docs[0].id
      };
    }
  }
};

export const forgotPassword = async (email: string) => {
  await sendPasswordResetEmail(auth, email);
};

export const getUserRoles = async (userId: string) => {
  const roles = await getDocs(
    query(
      collection(db, collectionNames.ROLES),
      where("userId", "==", userId),
      limit(1)
    )
  );

  if (roles.docs.length > 0) {
    return roles.docs[0].data().name;
  } else {
    return 'user'
  }
  
};

export const getUsersWithEmail = async (email: string) => {
  const users = await getDocs(
    query(collection(db, collectionNames.USERS), where("email", "==", email))
  );

  return users.docs.map((doc) => {
    return { email: doc.data().email, name: doc.data().name, id: doc.id };
  });
};

export const deleteUsersWithMultipleIds = async (ids: string[]) => {
  await deleteDocument("USERS", ids);
};

export const findScooterWithMacAddress: (macAddress: string) => Promise<
  {
    macAddress: any;
    id: string;
  }[]
> = async (macAddress: string) => {

  const scooters = await getDocs(
    query(
      collection(db, collectionNames.SCOOTERS),
      where("mac", "==", macAddress),
    )
  );

  return scooters.docs.map((doc) => {
    return { macAddress: doc.data().mac, id: doc.id };
  });
};

interface scooterDetails {
  // Define scooter properties here
  id: string;
  macAddress: string;
  // Add other properties as necessary
}

interface userDetails {
  // Define user properties here
  id: string;
  name: string;
  email: string;
  correct: boolean;
  // Add other properties as necessary
}

export const byPassPinWithScooter: (
  scooterDetails: scooterDetails[],
  userDetails: userDetails
) => Promise<{ result: boolean, }[]> = async (scooterDetails, userDetails) => {

  if (scooterDetails.length > 1) {
    const otherScooters = scooterDetails.slice(1);
    const otherScootersID = otherScooters.map((doc) => doc.id);
    await deleteMultipleDocuments("scooters", otherScootersID);
  }
  // Your existing logic or comments

  const correctScooter = scooterDetails[0]
  const correctScooterID = correctScooter.id
  const scooterDocumentRef = doc(db, 'scooters', correctScooterID);
  const userDocumentRef = doc(db, 'users', userDetails.id);

  const fieldPath = `scooter.${correctScooterID}`;
  const data = {[fieldPath]: '0000'}

  const ownerData = {
    owner: userDetails.id,
    pin: '0000'
  }

  try {
    await updateDoc(scooterDocumentRef, ownerData);
  } catch (error) {
    return [{ result: false }];; // Re-throw or handle error as needed
  }

  try {
    await updateDoc(userDocumentRef, data);
  } catch (error) {
    return [{ result: false }]; // Re-throw or handle error as needed
  }

  // Return an array containing objects with the 'delete' property
  return [{ result: true }];
};

export const wipeScooterPin: (
  macAddress: string,
) => Promise<{ result: boolean, scooterFound: boolean}[]> = async (macAddress: string) => {

  const scooters = await findScooterWithMacAddress(macAddress)

  if (scooters.length > 1) {
    const otherScooters = scooters.slice(1);
    const otherScootersID = otherScooters.map((doc) => doc.id);
    await deleteMultipleDocuments("scooters", otherScootersID);
  } else if (scooters.length === 0) {
    return [{result: false, scooterFound: false}]
  }

  const correctScooter = scooters[0]
  const correctScooterID = correctScooter.id
  const scooterDocumentRef = doc(db, 'scooters', correctScooterID);

  const ownerData = {
    owner: deleteField(),
    pin: deleteField(),
  }

  try {
    await updateDoc(scooterDocumentRef, ownerData);
  } catch (error) {
    return [{ result: false, scooterFound: true }];; // Re-throw or handle error as needed
  }

  return[{result: true, scooterFound: true}]
}